import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import styled, { createGlobalStyle } from 'styled-components';
import { colors } from './styles/colors';
import { typography } from './styles/typography';
import { HeroSection } from './sections/HeroSection';
import { MentorSection } from './sections/MentorSection';
import { FeatureSection } from './sections/FeatureSection';
import { DemoSectionDesktop } from './sections/DemoSectionDesktop';
import { DemoSectionMobile } from './sections/DemoSectionMobile';
import { Footer } from './sections/Footer';
import { DownloadPage } from './pages/DownloadPage';
import { trackPageView, trackSectionView } from './utils/analytics';

const GlobalStyle = createGlobalStyle`
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  body {
    font-family: ${typography.fontFamily};
    color: ${colors.textPrimary};
    background-color: ${colors.background};
    line-height: 1.6;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

  html {
    scroll-behavior: smooth;
  }

  a {
    text-decoration: none;
    color: inherit;
  }

  button {
    border: none;
    background: none;
    cursor: pointer;
  }

  img {
    max-width: 100%;
    height: auto;
  }

  /* 스크롤바 스타일링 */
  ::-webkit-scrollbar {
    width: 8px;
  }

  ::-webkit-scrollbar-track {
    background: ${colors.backgroundSecondary};
  }

  ::-webkit-scrollbar-thumb {
    background: ${colors.borderPrimary};
    border-radius: 4px;
  }

  ::-webkit-scrollbar-thumb:hover {
    background: ${colors.secondary};
  }
`;

const AppContainer = styled.div`
  min-height: 100vh;
`;

// 메인 랜딩 페이지 컴포넌트
const LandingPage: React.FC<{ isMobile: boolean }> = ({ isMobile }) => {
  // Analytics: 섹션별 스크롤 추적
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const sectionName = entry.target.getAttribute('data-section');
            if (sectionName) {
              trackSectionView(sectionName);
            }
          }
        });
      },
      { threshold: 0.5 }
    );

    // 섹션들에 observer 추가
    const sections = document.querySelectorAll('[data-section]');
    sections.forEach((section) => observer.observe(section));

    return () => observer.disconnect();
  }, []);

  return (
    <>
      <HeroSection />
      <MentorSection />
      <FeatureSection />
      {isMobile ? <DemoSectionMobile /> : <DemoSectionDesktop />}
      <Footer />
    </>
  );
};

function App() {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    // 초기 체크
    checkScreenSize();

    // 리사이즈 이벤트 리스너
    window.addEventListener('resize', checkScreenSize);

    // 클린업
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Analytics: 페이지뷰 추적
  useEffect(() => {
    trackPageView('tobe AI Landing Page');
  }, []);

  return (
    <>
      <GlobalStyle />
      <Router>
        <AppContainer>
          <Routes>
            <Route path="/" element={<LandingPage isMobile={isMobile} />} />
            <Route path="/download" element={<DownloadPage />} />
          </Routes>
        </AppContainer>
      </Router>
    </>
  );
}

export default App;
